import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

/**
 * Componente para la paginación superior de tablas y listados
 *
 * Este componente proporciona controles de paginación en la parte superior
 * de las tablas, permitiendo navegar entre páginas y cambiar el límite de registros.
 *
 * @example
 * <app-paginacion-superior
 *   [paginaActual]="paginaActual"
 *   [totalPaginas]="totalPaginas"
 *   [totalRegistros]="totalRegistros"
 *   [limite]="limite"
 *   [opcionesLimite]="[10, 25, 50]"
 *   [textoRegistros]="'productos'"
 *   [textoMostrar]="'Mostrar'"
 *   (paginaAnterior)="onPaginaAnterior()"
 *   (paginaSiguiente)="onPaginaSiguiente()"
 *   (cambiarLimite)="onCambiarLimite($event)">
 * </app-paginacion-superior>
 *
 * @since 1.0.0
 */
@Component({
    selector: 'app-paginacion-superior',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './paginacion-superior.html',
    styleUrls: ['./paginacion-superior.css'],
})
export class PaginacionSuperiorComponent {
    /**
     * Número de la página actual
     */
    @Input() paginaActual = 1;

    /**
     * Total de páginas disponibles
     */
    @Input() totalPaginas = 0;

    /**
     * Total de registros en la consulta
     */
    @Input() totalRegistros = 0;

    /**
     * Número de registros mostrados por página
     */
    @Input() limite = 10;

    /**
     * Opciones disponibles para el límite de registros por página
     */
    @Input() opcionesLimite: number[] = [5, 10, 20, 50];

    /**
     * Texto personalizado para referirse a los registros
     */
    @Input() textoRegistros = 'registros';

    /**
     * Texto personalizado para el label "Mostrar"
     */
    @Input() textoMostrar = 'Mostrar';

    /**
     * Evento emitido cuando se solicita navegar a la página anterior
     */
    @Output() paginaAnterior = new EventEmitter<void>();

    /**
     * Evento emitido cuando se solicita navegar a la página siguiente
     */
    @Output() paginaSiguiente = new EventEmitter<void>();

    /**
     * Evento emitido cuando se cambia el límite de registros por página
     */
    @Output() cambiarLimite = new EventEmitter<number>();

    /**
     * Evento emitido para mostrar el boton Relacionar Productos
     */
    @Input() mostrarEnlaceRelacionar = true;

    /**
     * Maneja la navegación a la página anterior
     */
    onPaginaAnterior() {
        this.paginaAnterior.emit();
    }

    /**
     * Maneja la navegación a la página siguiente
     */
    onPaginaSiguiente() {
        this.paginaSiguiente.emit();
    }

    /**
     * Maneja el cambio en el límite de registros por página
     */
    onCambiarLimite() {
        this.cambiarLimite.emit(this.limite);
    }
}
